import { useMemo } from "react";
import { Loader2, RefreshCw, Server } from "lucide-react";
import { useStatus, useIncidents } from "@/api/client";
import type { StatusComponent, StatusIncident } from "@/api/client";
import OverallStatus from "@/components/OverallStatus";
import ComponentCard from "@/components/ComponentCard";
import IncidentTimeline from "@/components/IncidentTimeline";

function groupComponents(
  components: StatusComponent[]
): Map<string, StatusComponent[]> {
  const groups = new Map<string, StatusComponent[]>();

  for (const comp of components) {
    const group = comp.group ?? "Other";
    const list = groups.get(group);
    if (list) {
      list.push(comp);
    } else {
      groups.set(group, [comp]);
    }
  }

  return groups;
}

function splitIncidents(incidents: StatusIncident[]): {
  active: StatusIncident[];
  resolved: StatusIncident[];
} {
  const active: StatusIncident[] = [];
  const resolved: StatusIncident[] = [];

  for (const incident of incidents) {
    if (incident.status === "resolved") {
      resolved.push(incident);
    } else {
      active.push(incident);
    }
  }

  return { active, resolved };
}

function SkeletonList({ count }: { count: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton h-16 w-full rounded-lg" />
      ))}
    </div>
  );
}

export default function StatusPage() {
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useStatus();

  const {
    data: allIncidents,
    isLoading: incidentsLoading,
  } = useIncidents();

  const componentGroups = useMemo(() => {
    if (!status) return new Map<string, StatusComponent[]>();
    return groupComponents(status.components);
  }, [status]);

  const { active: activeIncidents, resolved: resolvedIncidents } = useMemo(
    () => splitIncidents(allIncidents ?? status?.active_incidents ?? []),
    [allIncidents, status?.active_incidents]
  );

  if (statusLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-16 w-full rounded-lg" />
        <SkeletonList count={4} />
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="flex flex-col items-center py-12">
        <p className="text-sm text-gray-500">Failed to load status data.</p>
        <button
          onClick={() => refetchStatus()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Status</h1>
        <p className="mt-1 text-sm text-gray-500">
          Real-time status and uptime for all monitored services.
        </p>
      </div>

      {/* Overall status banner */}
      <OverallStatus status={status.overall_status} />

      {/* Components by group */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Components</h2>

        {componentGroups.size === 0 ? (
          <div className="card py-8 text-center text-sm text-gray-400">
            No components configured.
          </div>
        ) : (
          <div className="space-y-6">
            {Array.from(componentGroups.entries()).map(
              ([groupName, components]) => (
                <div key={groupName} className="card overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/50 px-4 py-3 sm:px-6">
                    <Server className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-700">
                      {groupName}
                    </h3>
                    <span className="text-xs text-gray-400">
                      ({components.length})
                    </span>
                  </div>
                  <div>
                    {components.map((comp) => (
                      <ComponentCard
                        key={comp.id}
                        component={comp}
                        showUptime={true}
                      />
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </section>

      {/* Active incidents */}
      {activeIncidents.length > 0 && (
        <section>
          <IncidentTimeline
            incidents={activeIncidents}
            title="Active Incidents"
          />
        </section>
      )}

      {/* Resolved incidents */}
      {!incidentsLoading && resolvedIncidents.length > 0 && (
        <section>
          <IncidentTimeline
            incidents={resolvedIncidents.slice(0, 10)}
            title="Past Incidents"
          />
        </section>
      )}

      {incidentsLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      )}

      {/* Last updated */}
      <div className="text-center text-xs text-gray-400">
        Status refreshes automatically every 60 seconds.
      </div>
    </div>
  );
}
